import { useEffect, useRef, useState } from "react";

const AVAILABILITY_CHECK_DEBOUNCE_MS = 350;

/**
 * Custom hook for patient ID validation
 * Handles validation logic, API calls, and state management for patient ID input
 *
 * @param {Object} config - Configuration object
 * @param {number} config.patientIdMinLength - Minimum length for patient ID
 * @param {number} config.patientIdLength - Maximum length for patient ID
 * @param {string} config.patientIdType - Type of patient ID (NUMERIC, ALPHA, ALPHANUMERIC, etc.)
 * @param {Function} config.checkIdentifier - API function to check if patient exists
 * @param {Function} config.getAttributes - API function to get patient attributes
 *
 * @returns {Object} Validation state and handlers
 */
export const usePatientValidation = ({
    patientIdMinLength,
    patientIdLength,
    patientIdType,
    checkIdentifier,
    getAttributes
}) => {
    const [patientId, setPatientId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [existingPatientData, setExistingPatientData] = useState(null);
    const [isPatientAdmitted, setIsPatientAdmitted] = useState(false);

    // The availability check is debounced and async; results are only applied
    // if the input still holds the value the check was started for.
    const debounceTimerRef = useRef(null);
    const latestValueRef = useRef("");

    useEffect(() => () => clearTimeout(debounceTimerRef.current), []);

    const regexMap = {
        NUMERIC: /^[0-9]+$/,
        ALPHA: /^[a-zA-Z]+$/,
        ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
        ALPHA_LOWER: /^[a-z]+$/,
        ALPHA_UPPER: /^[A-Z]+$/,
        ALPHANUMERIC_UPPER: /^[A-Z0-9]+$/,
        ALPHANUMERIC_LOWER: /^[a-z0-9]+$/,
    };

    const typeValidationMessages = {
        ALPHA: "Patient ID must only contain letters",
        ALPHANUMERIC: "Patient ID must only contain letters and numbers",
        NUMERIC: "Patient ID must only contain numbers",
        ALPHA_LOWER: "Patient ID must only contain lowercase letters",
        ALPHA_UPPER: "Patient ID must only contain uppercase letters",
        ALPHANUMERIC_UPPER: "Patient ID must only contain uppercase letters or numbers",
        ALPHANUMERIC_LOWER: "Patient ID must only contain lowercase letters or numbers",
    };

    const runAvailabilityCheck = async (value) => {
        try {
            const res = await checkIdentifier(value);
            if (latestValueRef.current !== value) return;

            if (res.exist && res.patient_key) {
                // Patient exists - fetch detailed attributes
                const patientAttrs = await getAttributes(res.patient_key);
                if (latestValueRef.current !== value) return;

                // Check if patient is admitted
                if (patientAttrs.is_admitted) {
                    setIsPatientAdmitted(true);
                    setValidationMessage("Patient ID cannot be used because it corresponds to an admitted patient");
                    setIsValid(false);
                } else {
                    // Patient is discharged - allow selection
                    setExistingPatientData({
                        patient_key: patientAttrs.patient_key,
                        patient_id: patientAttrs.patient_id,
                        patient_name: patientAttrs.patient_name || "Unknown"
                    });
                    setValidationMessage("Patient ID already exists");
                    setIsValid(false);
                }
            } else {
                setValidationMessage("Patient ID is available");
                setIsValid(true);
            }
        } catch (err) {
            if (latestValueRef.current !== value) return;
            setValidationMessage("Error checking patient ID. Please try again.");
            setIsValid(false);
        } finally {
            if (latestValueRef.current === value) {
                setIsLoading(false);
            }
        }
    };

    const validatePatientId = async (value) => {
        // Echo the keystroke immediately; validation is a side effect and must
        // never gate the controlled input's value.
        latestValueRef.current = value;
        setPatientId(value);
        clearTimeout(debounceTimerRef.current);

        // Clear existing patient data when user changes input
        setExistingPatientData(null);
        setIsPatientAdmitted(false);

        const effectiveType = regexMap[patientIdType] ? patientIdType : "NUMERIC";
        const regex = regexMap[effectiveType];

        // Type validation
        if (!regex.test(value)) {
            setIsLoading(false);
            setValidationMessage(typeValidationMessages[effectiveType]);
            setIsValid(false);
            return;
        }

        // Length validation
        const minLength = Math.min(patientIdMinLength || patientIdLength, patientIdLength);
        if (value.length < minLength || value.length > patientIdLength) {
            setIsLoading(false);
            setValidationMessage(minLength === patientIdLength
                ? `Patient ID must be ${patientIdLength} characters`
                : `Patient ID must be ${minLength}-${patientIdLength} characters`);
            setIsValid(false);
            return;
        }

        // Debounced availability check; the ID is not valid until the check
        // for the current value has completed.
        setIsValid(false);
        setValidationMessage("");
        setIsLoading(true);
        debounceTimerRef.current = setTimeout(() => {
            runAvailabilityCheck(value);
        }, AVAILABILITY_CHECK_DEBOUNCE_MS);
    };

    const reset = () => {
        clearTimeout(debounceTimerRef.current);
        latestValueRef.current = "";
        setPatientId("");
        setIsLoading(false);
        setValidationMessage("");
        setIsValid(true);
        setExistingPatientData(null);
        setIsPatientAdmitted(false);
    };

    return {
        patientId,
        isLoading,
        validationMessage,
        isValid,
        existingPatientData,
        isPatientAdmitted,
        validatePatientId,
        reset
    };
};
